import { createPortal } from 'react-dom';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ASSIGN_TOOL_ID } from 'infrastructure/consts';
import PersonWorkspaceIcon from 'assets/img/person-workspace.svg?react';
import { useGetAssignToolData } from 'infrastructure/hooks/rpm/assign-tool/use-get-assign-tool';
import BigSearchIcon from 'assets/img/big-search.svg?react';
import ChevronRightIcon from 'components/atoms/icons/chevron-right.svg?react';
import classNames from 'classnames';
import useUser from 'utils/useUser';
import BaseButton from 'components/atoms/baseButton';
import { useLocation } from 'react-router-dom';
import { Routes } from 'router/routes';

import AssignToolTable from './table';
import s from './styles.module.scss';

interface AssignPatientsToolProps {}

const AssignPatientsTool: FC<AssignPatientsToolProps> = () => {
  const [open, setOpen] = useState<null | boolean>(null);
  const { data, isLoading } = useGetAssignToolData();
  const { t } = useTranslation();
  const { isMobileDevice } = useUser();

  const toggle = () =>
    setOpen((prev) => {
      if (isMobileDevice) {
        document.body?.classList.toggle(s['no-scroll']);
      }
      return !prev;
    });

  return createPortal(
    <div
      id={ASSIGN_TOOL_ID}
      data-open={open}
      className={classNames(s.wrapper, { [s.open]: open })}
    >
      <div className={s['trigger-wrapper']} onClick={toggle}>
        <PersonWorkspaceIcon className={s.icon} />
        <p>
          {!isMobileDevice && t('labels.assigned')} ({data?.count ?? 0})
        </p>
      </div>
      <div className={s.header}>
        {isMobileDevice && (
          <BaseButton
            icon={<ChevronRightIcon />}
            onClick={toggle}
            type="secondary"
          />
        )}
        <h2 className={s.title}>
          {t('overviewDashboard.assignedPatientList')}
        </h2>
      </div>
      <div className={s.body}>
        {!data?.count ? (
          <div className={s.empty}>
            <BigSearchIcon className={s['empty-icon']} />
            <p>{t('messages.assignToolEmptyMessage')}</p>
          </div>
        ) : (
          <AssignToolTable
            patients={data?.data}
            isLoading={isLoading}
            toggleOpen={toggle}
          />
        )}
      </div>
    </div>,
    document.getElementById('root')!,
  );
};

const AssignPatientsToolWrapper = () => {
  const {
    isAgencyDoctor,
    isCriticalOrAtRiskAlertingRole,
    isFullyManagedAgency,
  } = useUser();

  const location = useLocation();
  const isOverviewDashboardRoute =
    location.pathname === Routes.overviewDashboard;

  const hideTool = !isAgencyDoctor || isCriticalOrAtRiskAlertingRole;

  if (hideTool) return null;

  // temporary show only for Fully Managed Agency
  if (isFullyManagedAgency) return <AssignPatientsTool />;

  // temporary hide for non Overview Dashboard and non Fully Managed Agency
  if (!isOverviewDashboardRoute) return null;

  return <AssignPatientsTool />;
};

export default AssignPatientsToolWrapper;
